import { formatDateTime } from '../../lib/utils';
import DisclosureQuickStatsBar from './DisclosureQuickStatsBar';
import PrfRecordFullView, { PaperPatientFullView } from './PrfRecordFullView';
import type { EventDisclosureReport } from '../../types/disclosure';

function fmt(ts: string | null | undefined) {
  if (!ts) return '—';
  return formatDateTime(ts);
}

function formTypeLabel(formType: string) {
  if (formType === 'minor_injury') return 'Minor Injury';
  if (formType === 'paracetamol_screening') return 'Paracetamol Screening';
  return 'Patient Report Form';
}

function resourceTypeLabel(type: string | null | undefined) {
  if (!type) return '—';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

interface EventDisclosureReportViewProps {
  report: EventDisclosureReport;
}

export default function EventDisclosureReportView({
  report,
}: EventDisclosureReportViewProps) {
  const { event, resources, incidents, summary } = report;

  return (
    <div className="disclosure-report max-w-4xl mx-auto">
      <header className="border-b border-border pb-4 mb-6">
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
          Event Disclosure Report — {report.detailLevel === 'full' ? 'Full report' : 'Short report'}
        </p>
        <h1 className="text-xl font-bold mt-1">{event.name}</h1>
        <p className="text-xs font-mono text-muted-foreground mt-0.5">{event.id}</p>
        <p className="text-[11px] text-muted-foreground mt-2">
          Generated {fmt(report.generatedAt)}
        </p>
        {report.detailLevel === 'full' && summary.fullOptions && (
          <p className="text-[11px] text-muted-foreground mt-2 border border-border rounded-lg px-3 py-2">
            Full sections included:{' '}
            {[
              summary.fullOptions.incidents && 'incident details & logs',
              summary.fullOptions.resources && 'resource MDT data',
              summary.fullOptions.patientIds.length > 0 &&
                `${summary.fullOptions.patientIds.length} patient record(s)`,
            ]
              .filter(Boolean)
              .join(' · ') || 'none'}
          </p>
        )}
      </header>

      <DisclosureQuickStatsBar report={report} />

      <section className="mb-8">
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Event Details
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Category</dt>
            <dd>{event.category}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Status</dt>
            <dd className="uppercase">{event.status}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event date</dt>
            <dd>{event.eventDate || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Start time</dt>
            <dd>{event.eventStartTime || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event opened</dt>
            <dd>{fmt(event.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event closed</dt>
            <dd>{fmt(event.closedAt)}</dd>
          </div>
          {event.closeReason && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Close reason</dt>
              <dd>{event.closeReason}</dd>
            </div>
          )}
          {event.location?.address && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Location</dt>
              <dd>{event.location.address}</dd>
            </div>
          )}
          {event.description && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Description</dt>
              <dd className="whitespace-pre-wrap">{event.description}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Resources ({summary.resourceCount})
        </h2>
        {resources.length === 0 ? (
          <p className="text-sm text-muted-foreground">No resources recorded for this event.</p>
        ) : resources.some((r) => r.mdtSessions?.length || r.mdtUsername) ? (
          <div className="space-y-4">
            {resources.map((r) => (
              <div key={r.id} className="border border-border rounded-lg p-3 text-xs">
                <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
                  <span className="font-mono font-semibold">{r.callsign}</span>
                  <span>{resourceTypeLabel(r.type)}</span>
                  {r.department && <span className="text-muted-foreground">{r.department}</span>}
                  {r.status && (
                    <span className="text-[10px] uppercase text-muted-foreground ml-auto">{r.status}</span>
                  )}
                </div>
                {(r.mdtUsername || r.mdtDisplayName) && (
                  <p className="text-muted-foreground mb-2">
                    Current MDT user:{' '}
                    <span className="text-foreground font-mono">{r.mdtUsername || r.mdtDisplayName}</span>
                    {r.mdtDisplayName && r.mdtUsername && r.mdtDisplayName !== r.mdtUsername && (
                      <span> ({r.mdtDisplayName})</span>
                    )}
                  </p>
                )}
                {r.mdtSessions && r.mdtSessions.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-1.5">
                      MDT sign-on history
                    </h4>
                    <div className="space-y-1">
                      {r.mdtSessions.map((s, i) => (
                        <p key={`${s.timestamp}-${i}`} className="text-[11px] border-l-2 border-border pl-2">
                          <span className="font-mono text-muted-foreground">{fmt(s.timestamp)}</span>
                          <span className="mx-1.5 font-semibold">{s.action}</span>
                          {s.username && <span className="font-mono">{s.username}</span>}
                          {s.details && <span className="text-muted-foreground ml-1">— {s.details}</span>}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <table className="w-full text-xs border-collapse disclosure-table">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-3">Callsign</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2">Department</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono">{r.callsign}</td>
                  <td className="py-2 pr-3">{resourceTypeLabel(r.type)}</td>
                  <td className="py-2">{r.department || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Incidents ({summary.incidentCount})
        </h2>
        {incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No incidents recorded for this event.</p>
        ) : (
          <div className="space-y-8">
            {incidents.map((inc) => (
              <article key={inc.id} className="border border-border rounded-lg p-4 break-inside-avoid">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
                  <h3 className="font-mono font-bold text-sm">{inc.id}</h3>
                  <span className="text-sm">{inc.title}</span>
                  <span className="text-[10px] uppercase text-muted-foreground ml-auto">{inc.status}</span>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-4">
                  <div>
                    <dt className="text-[10px] uppercase text-muted-foreground">Opened</dt>
                    <dd>{fmt(inc.reportedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-muted-foreground">Closed</dt>
                    <dd>{fmt(inc.closedAt)}</dd>
                  </div>
                  {inc.closeResult && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Result</dt>
                      <dd>{inc.closeResult}</dd>
                    </div>
                  )}
                  {inc.location?.address && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Location</dt>
                      <dd>{inc.location.address}</dd>
                    </div>
                  )}
                  {inc.description && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Description</dt>
                      <dd className="whitespace-pre-wrap">{inc.description}</dd>
                    </div>
                  )}
                  {inc.tags && inc.tags.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Tags</dt>
                      <dd>{inc.tags.join(', ')}</dd>
                    </div>
                  )}
                  {inc.assignedUnits && inc.assignedUnits.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Assigned units</dt>
                      <dd className="font-mono">{inc.assignedUnits.join(', ')}</dd>
                    </div>
                  )}
                </dl>

                {inc.assignments.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Resource assignments
                    </h4>
                    <table className="w-full text-[11px] border-collapse disclosure-table">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-1.5 pr-2">Callsign</th>
                          <th className="py-1.5 pr-2">Type</th>
                          <th className="py-1.5 pr-2">Assigned</th>
                          <th className="py-1.5 pr-2">En route</th>
                          <th className="py-1.5 pr-2">On scene</th>
                          <th className="py-1.5">Cleared</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inc.assignments.map((a) => (
                          <tr key={a.callsign} className="border-b border-border/50">
                            <td className="py-1.5 pr-2 font-mono">{a.callsign}</td>
                            <td className="py-1.5 pr-2">{resourceTypeLabel(a.resourceType)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.assignedAt)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.enRouteAt)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.onSceneAt)}</td>
                            <td className="py-1.5 font-mono">{fmt(a.clearedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {inc.prfs.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Patient records ({inc.prfs.length})
                    </h4>
                    <div className="space-y-3">
                      {inc.prfs.map((prf) => {
                        const isFullDigital = !!prf.fullData;
                        const isFullPaper =
                          !!prf.isPaper &&
                          report.detailLevel === 'full' &&
                          summary.fullOptions?.patientIds.includes(prf.id);

                        return (
                        <div key={prf.id} className="rounded-md bg-muted/40 p-3 text-xs break-inside-avoid">
                          {!isFullDigital && !isFullPaper && (
                          <>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1.5">
                            <span className="font-mono font-semibold">{prf.id}</span>
                            <span className="text-muted-foreground">{formTypeLabel(prf.formType)}</span>
                            {prf.isPaper && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-semibold">
                                Paper
                              </span>
                            )}
                            {prf.referenceOnly && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                                Reference only
                              </span>
                            )}
                            <span className="text-muted-foreground ml-auto">{fmt(prf.submittedAt)}</span>
                          </div>

                          {prf.referenceOnly ? (
                            <p className="text-muted-foreground">
                              Reference recorded — clinical detail requires <strong>prfs:view</strong> permission.
                            </p>
                          ) : (
                            <>
                              {report.detailLevel === 'full' && prf.patientName && (
                                <p>
                                  <span className="text-muted-foreground">Patient / notes: </span>
                                  {prf.patientName}
                                </p>
                              )}
                              {prf.injuryType && (
                                <p>
                                  <span className="text-muted-foreground">Injury / complaint: </span>
                                  {prf.injuryType}
                                </p>
                              )}
                              {prf.resultingAction && (
                                <p>
                                  <span className="text-muted-foreground">Resulting action: </span>
                                  {prf.resultingAction}
                                </p>
                              )}
                              {prf.dealtWithBy && prf.dealtWithBy.length > 0 && (
                                <p>
                                  <span className="text-muted-foreground">Dealt with by: </span>
                                  {prf.dealtWithBy.join(', ')}
                                </p>
                              )}
                              <p className="text-muted-foreground mt-1">Submitted by {prf.submittedBy}</p>
                            </>
                          )}
                          </>
                          )}

                          {isFullDigital && (
                            <PrfRecordFullView
                              prfId={prf.id}
                              incidentId={inc.id}
                              eventId={event.id}
                              submittedAt={prf.submittedAt}
                              submittedBy={prf.submittedBy}
                              data={prf.fullData as Record<string, unknown>}
                              accessLogs={prf.accessLogs}
                              disclosureLogs={prf.disclosureLogs}
                              embedded
                            />
                          )}

                          {isFullPaper && (
                            <PaperPatientFullView
                              prfId={prf.id}
                              formType={prf.formType}
                              notes={prf.notes || prf.patientName || undefined}
                              submittedAt={prf.submittedAt}
                              submittedBy={prf.submittedBy}
                              incidentId={inc.id}
                              eventId={event.id}
                            />
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {inc.logs && inc.logs.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Incident log &amp; remarks ({inc.logs.length})
                    </h4>
                    <div className="space-y-1.5 max-h-96 overflow-y-auto print:max-h-none print:overflow-visible">
                      {inc.logs.map((log) => (
                        <div key={log.id} className="text-[11px] border-l-2 border-border pl-2 py-0.5">
                          <span className="font-mono text-muted-foreground">{fmt(log.timestamp)}</span>
                          <span className="mx-1.5 text-primary font-semibold">{log.action}</span>
                          <span className="text-muted-foreground">{log.userName}</span>
                          <p className="text-foreground mt-0.5 whitespace-pre-wrap">{log.details}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {summary.patientNamesRedacted && (
        <p className="mt-6 text-[11px] text-muted-foreground border border-border rounded-lg px-3 py-2">
          Patient names in this shared report are redacted to initials. Full names are available only in
          Command or when a share link was created with “Include full patient names”.
        </p>
      )}

      {summary.clinicalDataIncluded === false && (
        <p className="mt-4 text-[11px] text-muted-foreground border border-border rounded-lg px-3 py-2">
          Patient records show unique reference numbers only — no clinical detail is included without
          appropriate permissions.
        </p>
      )}

      <footer className="mt-10 pt-4 border-t border-border text-[10px] text-muted-foreground print:hidden">
        CADSys Command — confidential event disclosure. Contains operational and patient-related data;
        handle in accordance with applicable data protection requirements.
      </footer>
    </div>
  );
}

const DISCLOSURE_PRINT_FOOTER_ID = 'disclosure-print-footer';

export function disclosurePrinterName(user: {
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
} | null | undefined): string {
  if (!user) return 'Unknown';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.username || user.email || 'Unknown';
}

function formatDisclosurePrintTime(date: Date): string {
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function printDisclosureReport(printedBy = 'Unknown') {
  document.body.classList.add('printing-disclosure');

  let footer = document.getElementById(DISCLOSURE_PRINT_FOOTER_ID);
  if (!footer) {
    footer = document.createElement('div');
    footer.id = DISCLOSURE_PRINT_FOOTER_ID;
    footer.className = 'disclosure-print-footer';
    document.body.appendChild(footer);
  }

  const printedAt = formatDisclosurePrintTime(new Date());
  footer.textContent = `CONFIDENTIAL - Report printed by "${printedBy}" ${printedAt} - handle in accordance with data protection policies.`;

  const cleanup = () => {
    document.body.classList.remove('printing-disclosure');
    footer?.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
}
